/**
 * "Vilken grupp tillhör du?" vid registrering som ansvarig. Svaret är fri
 * text som admin matchar mot rätt grupp när kontot godkänns. Det kopplar
 * aldrig kontot till en grupp av sig självt.
 */

export const REQUESTED_GROUP_MAX = 120;

/** Svaret som det sparas: trimmat, utan styrtecken, högst 120 tecken. Tomt blir null. */
export const cleanRequestedGroup = (value: unknown): string | null => {
    if (typeof value !== 'string') return null;
    const text = value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, REQUESTED_GROUP_MAX).trim();
    return text || null;
};

const tokens = (text: string): string[] =>
    [...new Set(text.toLocaleLowerCase('sv-SE').split(/[^\p{L}\p{N}]+/u).filter(Boolean))];

/**
 * Gruppen som bäst motsvarar svaret, eller null när ingen är tydligt bäst.
 *
 * Ord som finns i många grupper (t.ex. "IFK" och "Bankeryd") väger lite, ord
 * som bara finns i en grupp (t.ex. "F13") väger mycket. Poängen är hur stor
 * del av gruppnamnets vikt som finns i svaret. Lika bäst (t.ex. två grupper
 * med samma namn) ger null, så att admin väljer själv.
 */
export const bestGroupMatch = (answer: string | null | undefined, groups: { id: string; name: string }[]): string | null => {
    const wanted = new Set(tokens(answer || ''));
    if (wanted.size === 0 || groups.length === 0) return null;

    const groupTokens = groups.map(g => tokens(g.name));
    const seenIn = new Map<string, number>();
    groupTokens.forEach(ts => ts.forEach(t => seenIn.set(t, (seenIn.get(t) || 0) + 1)));
    const weight = (t: string) => 1 / (seenIn.get(t) || 1);

    let best: string | null = null;
    let bestScore = 0;
    let tie = false;
    groups.forEach((g, i) => {
        const ts = groupTokens[i];
        const total = ts.reduce((s, t) => s + weight(t), 0);
        if (total === 0) return;
        const score = ts.filter(t => wanted.has(t)).reduce((s, t) => s + weight(t), 0) / total;
        if (score > bestScore + 1e-9) { best = g.id; bestScore = score; tie = false; }
        else if (Math.abs(score - bestScore) <= 1e-9 && score > 0) tie = true;
    });
    return best && bestScore >= 0.5 && !tie ? best : null;
};
